// Pure maths for splitting a PO (or a reservation) across weeks. No Vue, no store: the
// composable feeds it plain data and renders whatever comes back.
//
// Every function walks types independently. A cable's LC and MMC ends are never coupled here
// by decision (spec, 2026-09-07): LC can finish in the first week while MMC uses five.

export type SplitPreset = 'fill' | 'even';

export interface SplitType {
  capacityTypeId: string;
  code: string;
  // The frozen plan quantity for this type; 0 means the order does not carry it.
  total: number;
}

export interface SplitBooking {
  poNumber: string | null;
  notes: string | null;
  quantity: number;
}

export interface SplitWeek {
  weekKey: string;
  label: string;
  // remainedCapacity per capacityTypeId; null = the week has no plan for that type.
  remainingByType: Record<string, number | null>;
  // The PO already has a block in this week (any type). Always false for a reservation.
  alreadyScheduled: boolean;
  // Existing blocks per type, drawn as grey segments. Never used in the maths.
  bookedByType: Record<string, SplitBooking[]>;
}

export type SplitCells = Record<string, Record<string, number>>;

export interface SplitOver {
  weekKey: string;
  capacityTypeId: string;
  by: number;
}

export interface SplitEvaluation {
  plannedWeekKeys: string[];
  placedByType: Record<string, number>;
  openByType: Record<string, number>;
  over: SplitOver[];
  lastWeekKey: string | null;
}

export function carriedTypes(types: SplitType[]): SplitType[] {
  return types.filter((type) => type.total > 0);
}

export function pluralWeeks(n: number): string {
  return `${n} ${n === 1 ? 'week' : 'weeks'}`;
}

export function eligibleFor(week: SplitWeek, type: SplitType, isReservation: boolean): boolean {
  if (week.remainingByType[type.capacityTypeId] === null || week.remainingByType[type.capacityTypeId] === undefined) return false;
  return isReservation || !week.alreadyScheduled;
}

// A week earns a card when at least one carried type can use it; otherwise it is a skipped row.
export function isListed(week: SplitWeek, types: SplitType[], isReservation: boolean): boolean {
  return carriedTypes(types).some((type) => eligibleFor(week, type, isReservation));
}

function cellOf(cells: SplitCells, weekKey: string, capacityTypeId: string): number {
  return cells[weekKey]?.[capacityTypeId] ?? 0;
}

function setCell(cells: SplitCells, weekKey: string, capacityTypeId: string, quantity: number): void {
  cells[weekKey] = { ...(cells[weekKey] ?? {}), [capacityTypeId]: quantity };
}

export function fillForward(types: SplitType[], weeks: SplitWeek[], isReservation: boolean): SplitCells {
  const cells: SplitCells = {};
  carriedTypes(types).forEach((type) => {
    let open = type.total;
    weeks.forEach((week) => {
      if (open <= 0 || !eligibleFor(week, type, isReservation)) return;
      const room = Math.max(week.remainingByType[type.capacityTypeId] ?? 0, 0);
      const quantity = Math.min(open, room);
      if (quantity <= 0) return;
      setCell(cells, week.weekKey, type.capacityTypeId, quantity);
      open -= quantity;
    });
  });
  return cells;
}

export function maxEvenWeeks(types: SplitType[], weeks: SplitWeek[], isReservation: boolean): number {
  return Math.max(1, ...carriedTypes(types).map((type) => weeks.filter((week) => eligibleFor(week, type, isReservation)).length));
}

// Equal shares per type over the first N weeks eligible for that type. The integer remainder
// goes one unit at a time to the LAST weeks so the sum is exact. Capacity is ignored on
// purpose: red cells are how the planner sees which weeks cannot take a flat share.
export function spreadEvenly(types: SplitType[], weeks: SplitWeek[], n: number, isReservation: boolean): SplitCells {
  const cells: SplitCells = {};
  carriedTypes(types).forEach((type) => {
    const targets = weeks.filter((week) => eligibleFor(week, type, isReservation)).slice(0, Math.max(1, n));
    if (targets.length === 0) return;
    const base = Math.floor(type.total / targets.length);
    const remainder = type.total - base * targets.length;
    targets.forEach((week, index) => {
      const extra = index >= targets.length - remainder ? 1 : 0;
      setCell(cells, week.weekKey, type.capacityTypeId, base + extra);
    });
  });
  return cells;
}

export function evaluate(types: SplitType[], weeks: SplitWeek[], cells: SplitCells): SplitEvaluation {
  const carried = carriedTypes(types);
  const plannedWeekKeys = weeks.map((week) => week.weekKey).filter((weekKey) => carried.some((type) => cellOf(cells, weekKey, type.capacityTypeId) > 0));
  const placedByType = Object.fromEntries(
    carried.map((type) => [type.capacityTypeId, plannedWeekKeys.reduce((sum, weekKey) => sum + cellOf(cells, weekKey, type.capacityTypeId), 0)])
  );
  const openByType = Object.fromEntries(carried.map((type) => [type.capacityTypeId, Math.max(0, type.total - placedByType[type.capacityTypeId])]));
  const over: SplitOver[] = [];
  weeks.forEach((week) => {
    carried.forEach((type) => {
      const remaining = week.remainingByType[type.capacityTypeId];
      if (remaining === null || remaining === undefined) return;
      // A week that was already over before the dialog opened is not this split's doing; it
      // counts as over only once the split places something there (spec: cell > 0 and cell > remaining).
      const cell = cellOf(cells, week.weekKey, type.capacityTypeId);
      const left = remaining - cell;
      if (cell > 0 && left < 0) over.push({ weekKey: week.weekKey, capacityTypeId: type.capacityTypeId, by: -left });
    });
  });
  return { plannedWeekKeys, placedByType, openByType, over, lastWeekKey: plannedWeekKeys.length > 0 ? plannedWeekKeys[plannedWeekKeys.length - 1] : null };
}

// A typed value can never push a type past what the PO has left: cap at open + the cell's own
// current value. Whole units only.
export function clampCell(types: SplitType[], cells: SplitCells, weekKey: string, capacityTypeId: string, raw: number): number {
  const type = types.find((candidate) => candidate.capacityTypeId === capacityTypeId);
  if (!type) return 0;
  const current = cellOf(cells, weekKey, capacityTypeId);
  const placedElsewhere = Object.keys(cells).reduce((sum, key) => sum + cellOf(cells, key, capacityTypeId), 0) - current;
  const cap = Math.max(0, type.total - placedElsewhere);
  const rounded = Math.round(Number.isFinite(raw) ? raw : 0);
  return Math.min(cap, Math.max(0, rounded));
}

// The carried type with the least room relative to how much the order needs of it — the one
// the problem sentence names ("has room for 1,000 of the 4,506 MMC").
export function tightestType(types: SplitType[], week: SplitWeek): SplitType | null {
  const candidates = carriedTypes(types).filter((type) => typeof week.remainingByType[type.capacityTypeId] === 'number');
  if (candidates.length === 0) return null;
  return candidates.reduce((tightest, type) => {
    const room = (week.remainingByType[type.capacityTypeId] as number) / type.total;
    const tightestRoom = (week.remainingByType[tightest.capacityTypeId] as number) / tightest.total;
    return room < tightestRoom ? type : tightest;
  });
}
